import { addCpfMask, EmailLink, getFirstLetter, WhatsAppLink } from '@repo/ui';
import { GetTeamReportListItem } from 'domain/models';
import { formatCurrency } from 'presentation/app/utils/format-currency';
import { useMemo, useState } from 'react';
import { Badge, Card, CardBody, CardFooter, Col, Row } from 'reactstrap';

const ITEMS_PER_PAGE = 9;

interface TeamListProps {
  professionals: GetTeamReportListItem[];
}

const employmentModeVariants: Record<string, string> = {
  Bolsista: 'bg-success-subtle text-success',
  CLT: 'bg-warning-subtle text-warning',
  'CLT Docente': 'bg-primary-subtle text-primary',
  'CLT Preceptor': 'bg-info-subtle text-info',
  Externo: 'bg-secondary-subtle text-secondary',
  Pesquisador: 'bg-dark-subtle text-body',
  PJ: 'bg-dark-subtle text-body',
};

export const TeamList = ({ professionals }: TeamListProps) => {
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);

  const data = useMemo(() => {
    return professionals.slice(0, visibleItems);
  }, [professionals, visibleItems]);

  const handleLoadMore = () => {
    setVisibleItems((prev) => prev + ITEMS_PER_PAGE);
  };

  return (
    <>
      <Row className="g-3 mb-4">
        {data.map((professional) => (
          <Col key={professional.id} xs={12} md={4} lg={4}>
            <Card className="h-100 border-0 shadow-sm card-animate mb-2">
              <CardBody>
                <div className="d-flex align-items-start gap-3">
                  {professional.photo ? (
                    <img
                      className="rounded-circle object-fit-cover"
                      src={professional.photo}
                      alt="photo"
                      width={72}
                      height={72}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex justify-content-center align-items-center"
                      style={{
                        width: '72px',
                        height: '72px',
                        backgroundColor: '#144c5b',
                      }}
                    >
                      <span className="text-white fs-5 fw-bold">
                        {getFirstLetter(professional.name)}
                      </span>
                    </div>
                  )}

                  <div className="flex-grow-1">
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                      <div>
                        <h5 className="mb-1 fs-15">
                          {professional.name || 'Não informado'}
                        </h5>
                        <span className="text-muted">
                          {addCpfMask(professional.cpf) || '-'}
                        </span>
                      </div>

                      {professional.employmentMode && (
                        <Badge
                          className={`fw-medium ${
                            employmentModeVariants[
                              professional.employmentMode
                            ] ?? 'bg-light text-body'
                          }`}
                        >
                          {professional.employmentMode}
                        </Badge>
                      )}
                    </div>

                    <div className="d-flex flex-column gap-2 text-muted fs-13">
                      <div className="d-flex align-items-center gap-2">
                        <i className="ri-mail-line fs-16"></i>

                        {professional.email ? (
                          <EmailLink email={professional.email} />
                        ) : (
                          '-'
                        )}
                      </div>

                      {professional.phone ? (
                        <WhatsAppLink telephone={professional.phone} />
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </div>
              </CardBody>

              <CardFooter className="bg-light-subtle border-top">
                <div className="d-flex justify-content-around text-center">
                  <div>
                    <div className="mb-1">
                      <i className="ri-team-line fs-18"></i>
                    </div>
                    <p className="mb-0 fw-semibold">{professional.groups}</p>
                    <small className="text-muted">Grupos</small>
                  </div>

                  <div>
                    <div className="mb-1">
                      <i className="ri-group-line fs-18"></i>
                    </div>
                    <p className="mb-0 fw-semibold">
                      {professional.studentsAssisted}
                    </p>
                    <small className="text-muted">Estudantes</small>
                  </div>

                  <div>
                    <div className="mb-1">
                      <i className="ri-time-line fs-18"></i>
                    </div>
                    <p className="mb-0 fw-semibold">
                      {professional.weeklyWorkload}
                    </p>
                    <small className="text-muted">CH Semanal</small>
                  </div>

                  <div>
                    <div className="mb-1">
                      <i className="ri-money-dollar-circle-line fs-18"></i>
                    </div>
                    <p className="mb-0 fw-semibold">
                      {formatCurrency(professional.amount) || '-'}
                    </p>
                    <small className="text-muted">Valor</small>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </Col>
        ))}
      </Row>

      {visibleItems < professionals.length && (
        <div className="d-flex justify-content-center mt-4">
          <button className="btn btn-light" onClick={handleLoadMore}>
            Mostrar mais
          </button>
        </div>
      )}
    </>
  );
};
